/**
 * 書式のエディタ（帳票デザインの2回目、docs/31 §9）の状態の道具。
 *   ・書式（PaperLayout）は不変のデータとして持ち、直すたびに新しいものを作る（元に戻す・やり直すはその履歴）
 *   ・座標は mm（小数1桁）。吸着（snap）は mm 単位
 */
import type { PaperBind, PaperEl, PaperLayout, PaperTable } from "@genbaryoku/contract";
import { PAPER_SIZES } from "@genbaryoku/contract";
import { r1 } from "@genbaryoku/paper";
import { useCallback, useRef, useState } from "react";

export type Sel = string | "table" | null;

/** 1mm を 96dpi で何 px に描くか（100% 表示＝実寸） */
export const PX_PER_MM = 96 / 25.4;

export const TYPE_NAME: Record<PaperEl["type"] | "table", string> = { text: "文字", field: "項目", line: "線", box: "枠", image: "画像", qr: "二次元", table: "明細表" };
export type RuleStyle = "none" | "solid" | "double" | "dotted" | "dashed";
export const RULE_STYLES: Array<[RuleStyle, string]> = [["none", "無し"], ["solid", "実線"], ["double", "二重線"], ["dotted", "点線"], ["dashed", "破線"]];
export const RULE_W = [0.1, 0.25, 0.5, 0.75, 1.0];
export const FONTS: Array<["serif" | "sans" | "maru", string]> = [["serif", "明朝"], ["sans", "ゴシック"], ["maru", "丸ゴシック"]];

let seq = 0;
export function newId(layout: PaperLayout): string {
  const used = new Set(layout.els.map((e) => e.id));
  let id = "";
  do id = `n${Date.now().toString(36)}${(++seq).toString(36)}`;
  while (used.has(id));
  return id;
}

export const snapTo = (v: number, snap: number): number => (snap > 0 ? Math.round(v / snap) * snap : r1(v));

/** 部品を1つ差し替えた新しい書式 */
export function updateEl(layout: PaperLayout, id: string, patch: Partial<PaperEl> | ((e: PaperEl) => PaperEl)): PaperLayout {
  return { ...layout, els: layout.els.map((e) => (e.id === id ? (typeof patch === "function" ? patch(e) : ({ ...e, ...patch } as PaperEl)) : e)) };
}
export function updateTable(layout: PaperLayout, patch: Partial<PaperTable> | ((t: PaperTable) => PaperTable)): PaperLayout {
  return { ...layout, table: typeof patch === "function" ? patch(layout.table) : { ...layout.table, ...patch } };
}

/** 部品を新しく置く（紙の左上の余白の内側） */
export function makeEl(layout: PaperLayout, kind: "text" | "field" | "line" | "vline" | "box", text?: string): PaperEl {
  const [mt, , , ml] = layout.page.margin;
  const id = newId(layout);
  const base = { id, on: true, locked: false, x: ml + 10, y: mt + 10 };
  switch (kind) {
    case "text": {
      const s = text || "文字を入れてください";
      const longest = Math.max(...s.split("\n").map((l) => l.length));
      const w = Math.min(layout.page.w - ml * 2, Math.max(20, r1(longest * 9.5 * 0.3528 * 1.05 + 3)));
      return { ...base, type: "text", name: text ? text.slice(0, 12) : "文字", frame: true, w, h: r1(6 * s.split("\n").length), text: s, font: "sans", size: 9.5, bold: false, align: "left", valign: "middle", color: "#1A1A18" };
    }
    case "field":
      return { ...base, type: "field", name: "項目", frame: false, w: 50, h: 6, bind: "projectName" as PaperBind, font: "sans", size: 9.5, bold: false, align: "left", valign: "middle", color: "#1A1A18" };
    case "line":
      return { ...base, type: "line", name: "横線", frame: true, w: 60, h: 0, dir: "h", style: "solid", lw: 0.25, color: "#1A1A18" };
    case "vline":
      return { ...base, type: "line", name: "縦線", frame: true, w: 0, h: 30, dir: "v", style: "solid", lw: 0.25, color: "#1A1A18" };
    case "box":
      return { ...base, type: "box", name: "枠", frame: true, w: 50, h: 20, style: "solid", lw: 0.5, color: "#1A1A18", fill: "" };
  }
}

/**
 * 画像の部品（046）。asset は 書式の画像の id か company:logo／company:seal。大きさは画像の縦横比で（幅 40mm を基準。下敷きは紙いっぱい）
 */
export function makeImageEl(layout: PaperLayout, asset: string, name: string, px: { width: number; height: number } | null, guide: boolean): PaperEl {
  const [mt, , , ml] = layout.page.margin;
  const id = newId(layout);
  if (guide) return { id, type: "image", name: name || "下敷き", on: true, frame: false, locked: false, x: 0, y: 0, w: layout.page.w, h: layout.page.h, asset, opacity: 0.5, keep: false, guide: true };
  const w = 40;
  const h = px && px.width > 0 ? r1((w * px.height) / px.width) : 20;
  return { id, type: "image", name: name || "画像", on: true, frame: false, locked: false, x: ml + 10, y: mt + 10, w, h: Math.max(3, Math.min(h, 120)), asset, opacity: 1, keep: true, guide: false };
}

/** 二次元コードの部品（046）。正方形 20mm。中身は固定の文字（会社の URL など）か項目 */
export function makeQrEl(layout: PaperLayout): PaperEl {
  const [mt, , mr] = layout.page.margin;
  const id = newId(layout);
  return { id, type: "qr", name: "二次元コード", on: true, frame: false, locked: false, x: r1(layout.page.w - mr - 20), y: mt, w: 20, h: 20, text: "", bind: null };
}

/** 向きを変える：幅と高さを入れ替えるだけ（部品は動かさない＝docs/31 §5 用紙） */
export function setOrient(layout: PaperLayout, orient: "portrait" | "landscape"): PaperLayout {
  if (orient === layout.orient) return layout;
  return { ...layout, orient, page: { ...layout.page, w: layout.page.h, h: layout.page.w } };
}
export function setPageSize(layout: PaperLayout, size: PaperLayout["page"]["size"]): PaperLayout {
  if (size === "custom") return { ...layout, page: { ...layout.page, size } };
  const [w, h] = PAPER_SIZES[size];
  const P = layout.orient === "portrait";
  return { ...layout, page: { ...layout.page, size, w: P ? w : h, h: P ? h : w } };
}

/** 元に戻す・やり直す（50手まで） */
export function useHistory(initial: PaperLayout | null) {
  const [layout, setLayoutRaw] = useState<PaperLayout | null>(initial);
  const hist = useRef<PaperLayout[]>([]);
  const redoStack = useRef<PaperLayout[]>([]);
  const [dirty, setDirty] = useState(false);
  const [, tick] = useState(0);
  /** 履歴に積んでから差し替える（ふつうの直し） */
  const commit = useCallback((next: PaperLayout | ((cur: PaperLayout) => PaperLayout)) => {
    setLayoutRaw((cur) => {
      if (!cur) return cur;
      const n = typeof next === "function" ? next(cur) : next;
      if (n === cur) return cur;
      hist.current.push(cur);
      if (hist.current.length > 50) hist.current.shift();
      redoStack.current = [];
      return n;
    });
    setDirty(true);
  }, []);
  /** 履歴に積まずに差し替える（読み込み・保存のあと） */
  const reset = useCallback((next: PaperLayout | null) => {
    setLayoutRaw(next);
    hist.current = [];
    redoStack.current = [];
    setDirty(false);
  }, []);
  const undo = useCallback(() => {
    setLayoutRaw((cur) => {
      const prev = hist.current.pop();
      if (!prev || !cur) return cur;
      redoStack.current.push(cur);
      return prev;
    });
    setDirty(true);
    tick((n) => n + 1);
  }, []);
  const redo = useCallback(() => {
    setLayoutRaw((cur) => {
      const nx = redoStack.current.pop();
      if (!nx || !cur) return cur;
      hist.current.push(cur);
      return nx;
    });
    setDirty(true);
    tick((n) => n + 1);
  }, []);
  return { layout, commit, reset, undo, redo, canUndo: hist.current.length > 0, canRedo: redoStack.current.length > 0, dirty, setDirty };
}
